import type { ProposalTextSectionKey } from "./proposalTextSections";
import type { ResolvedProposalSections } from "./proposalTextResolver";
import {
  DEFAULT_PROPOSAL_ALTERNATE_PAGES,
  isBuiltinProposalPageKind,
  PROPOSAL_BUILTIN_PAGE_KINDS,
  PROPOSAL_DOC_REF_PAGE_KIND,
  sortAlternatePages,
  type ProposalAlternate,
  type ProposalAlternatePage,
  type ProposalBuiltinPageKind,
  type ProposalManifestEntry,
} from "./proposalAlternateTypes";

const PAGE_TO_SECTIONS: Record<ProposalBuiltinPageKind, ProposalTextSectionKey[]> = {
  cover_letter: ["cover_letter_body"],
  cost_breakdown: ["cost_breakdown_intro", "cost_breakdown_footer"],
  payment_schedule: ["payment_schedule_intro", "payment_schedule_footer"],
  intent_to_purchase: ["intent_to_purchase_preamble", "intent_to_purchase_signature_block"],
};

/** Merge alternate page manifest into resolved text sections. */
export function applyAlternatePagesToSections(
  sections: ResolvedProposalSections,
  pages: readonly ProposalAlternatePage[],
): ResolvedProposalSections {
  const sorted = sortAlternatePages(pages);
  const next = { ...sections } as ResolvedProposalSections;
  for (const pageKind of PROPOSAL_BUILTIN_PAGE_KINDS) {
    const page = sorted.find((p) => p.page_kind === pageKind);
    const included = page?.included ?? false;
    if (included) continue;
    for (const sectionKey of PAGE_TO_SECTIONS[pageKind]) {
      next[sectionKey] = { ...next[sectionKey], included: false };
    }
  }
  return next;
}

/** Full manifest: built-in pages + attached Document Builder pages. */
export function buildProposalManifest(
  pages: readonly ProposalAlternatePage[],
  titlesByDocId: Record<string, string> = {},
): ProposalManifestEntry[] {
  return sortAlternatePages(pages)
    .filter((p) => p.included)
    .flatMap((page): ProposalManifestEntry[] => {
      if (page.page_kind === PROPOSAL_DOC_REF_PAGE_KIND && page.doc_document_id) {
        return [
          {
            type: "doc_ref",
            docDocumentId: page.doc_document_id,
            title: titlesByDocId[page.doc_document_id] ?? "Document",
          },
        ];
      }
      if (isBuiltinProposalPageKind(page.page_kind)) {
        return [{ type: "builtin", pageKind: page.page_kind }];
      }
      return [];
    });
}

/** Included built-in pages in render order. */
export function includedPagesInOrder(pages: readonly ProposalAlternatePage[]): ProposalBuiltinPageKind[] {
  return buildProposalManifest(pages)
    .filter((e): e is Extract<ProposalManifestEntry, { type: "builtin" }> => e.type === "builtin")
    .map((e) => e.pageKind);
}

/**
 * Explicit-match-only selection. Returns null when no key is given or
 * the key doesn't match — callers fall back to the synthetic Standard
 * manifest. Deliberately does NOT fall back to `is_default` / first DB
 * row: auto-picking an alternate the rep never chose is how incomplete
 * kit manifests produced blank PDFs (see dev_sync_and_fix.md 2026-07-16).
 */
export function pickProposalAlternate(
  alternates: readonly ProposalAlternate[],
  alternateKey: string | null | undefined,
): ProposalAlternate | null {
  const key = alternateKey?.trim();
  if (!key) return null;
  return alternates.find((a) => a.alternate_key === key) ?? null;
}

/**
 * Manifest-completeness guard. Returns a human-readable issue when an
 * alternate's included pages cannot produce any renderable output —
 * i.e. zero included pages, or the only included pages are doc_refs
 * without a document id. Alternates with issues must never be silently
 * rendered (that path produced blank PDFs); they are rejected at upsert
 * and hidden from the sales picker.
 */
export function proposalAlternateManifestIssue(
  pages: readonly ProposalAlternatePage[],
): string | null {
  const included = pages.filter((p) => p.included);
  if (included.length === 0) {
    return "This alternate has no included pages — include at least one page.";
  }
  const renderable = included.filter(
    (p) =>
      isBuiltinProposalPageKind(p.page_kind) ||
      (p.page_kind === PROPOSAL_DOC_REF_PAGE_KIND && !!p.doc_document_id),
  );
  if (renderable.length === 0) {
    return "This alternate's included pages have no content — attach a document or include a built-in page.";
  }
  return null;
}

export function normalizeAlternatePages(
  pages: readonly ProposalAlternatePage[] | undefined,
): ProposalAlternatePage[] {
  const byKind = new Map<ProposalBuiltinPageKind, ProposalAlternatePage>();
  for (const seed of DEFAULT_PROPOSAL_ALTERNATE_PAGES) {
    byKind.set(seed.page_kind as ProposalBuiltinPageKind, { ...seed });
  }
  const docPages: ProposalAlternatePage[] = [];
  for (const row of pages ?? []) {
    if (row.page_kind === PROPOSAL_DOC_REF_PAGE_KIND && row.doc_document_id) {
      docPages.push({
        page_kind: PROPOSAL_DOC_REF_PAGE_KIND,
        doc_document_id: row.doc_document_id,
        sort_order: Number.isFinite(row.sort_order) ? row.sort_order : docPages.length + 10,
        included: row.included !== false,
        proposal_page_id: null,
      });
      continue;
    }
    if (!isBuiltinProposalPageKind(row.page_kind)) continue;
    byKind.set(row.page_kind, {
      page_kind: row.page_kind,
      sort_order: Number.isFinite(row.sort_order) ? row.sort_order : 0,
      included: row.included !== false,
      proposal_page_id: row.proposal_page_id ?? null,
    });
  }
  return sortAlternatePages([...byKind.values(), ...docPages]);
}

export function docDocumentIdsFromPages(pages: readonly ProposalAlternatePage[]): string[] {
  return [
    ...new Set(
      pages
        .filter((p) => p.included && p.page_kind === PROPOSAL_DOC_REF_PAGE_KIND && p.doc_document_id)
        .map((p) => p.doc_document_id as string),
    ),
  ];
}
